import type { DiagramProps } from '@/lib/scad-feature-types';
import { D } from './DiagramStyles';

export function MirrorDiagram({ values }: DiagramProps) {
  const v = values.v ?? [1, 0, 0];
  const vx = v[0] ?? 1;
  const vy = v[1] ?? 0;

  // Determine mirror plane orientation based on normal vector
  // [1,0,0] → YZ plane (vertical), [0,1,0] → XZ plane (horizontal), [0,0,1] → XY plane
  const isHorizontal = vx === 0 && vy !== 0;

  const cx = 140; // center of mirror plane
  const cy = 75;

  // L-shape (asymmetric) points for "before" shape
  const srcPts = isHorizontal
    ? `${cx - 55},${cy + 10} ${cx - 55},${cy - 10} ${cx - 30},${cy - 10} ${cx - 30},${cy + 30} ${cx - 15},${cy + 30} ${cx - 15},${cy + 10}`
    : `${cx - 55},${cy + 30} ${cx - 55},${cy - 30} ${cx - 25},${cy - 30} ${cx - 25},${cy} ${cx - 10},${cy} ${cx - 10},${cy + 30}`;

  // Mirror of L-shape
  const mirPts = isHorizontal
    ? `${cx + 55},${cy - 10} ${cx + 55},${cy + 10} ${cx + 30},${cy + 10} ${cx + 30},${cy - 30} ${cx + 15},${cy - 30} ${cx + 15},${cy - 10}`
    : `${cx + 55},${cy - 30} ${cx + 55},${cy + 30} ${cx + 25},${cy + 30} ${cx + 25},${cy} ${cx + 10},${cy} ${cx + 10},${cy - 30}`;

  // Mirror plane line
  const planePts = isHorizontal
    ? { x1: cx - 65, y1: cy, x2: cx + 65, y2: cy }
    : { x1: cx, y1: cy - 50, x2: cx, y2: cy + 50 };

  // Normal vector arrow direction
  const normEndX = isHorizontal ? cx + 24 : cx + 20;
  const normEndY = isHorizontal ? cy - 20 : cy;

  const normalLabel = `[${vx}, ${vy}, ${v[2] ?? 0}]`;

  return (
    <svg viewBox="0 0 280 150" width="100%" style={{ display: 'block' }} aria-label="Mirror diagram">
      <defs>
        <marker id="arr-mir" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
          <path d="M0,1 L5,3 L0,5 Z" fill={D.DIM_STROKE} />
        </marker>
      </defs>

      {/* Original shape */}
      <polygon points={srcPts}
               fill={D.GEO_FILL} stroke={D.GEO_STROKE} strokeWidth="1.5" />

      {/* Mirror plane (dashed line) */}
      <line x1={planePts.x1} y1={planePts.y1} x2={planePts.x2} y2={planePts.y2}
            stroke="#a855f7" strokeWidth="1.5" strokeDasharray="5 3" opacity="0.8" />
      <text x={cx + 3} y={isHorizontal ? cy - 4 : cy - 38}
            fontSize="9" fontFamily="monospace" fill="#a855f7" opacity="0.85">mirror plane</text>

      {/* Mirrored shape */}
      <polygon points={mirPts}
               fill="rgba(96,165,250,0.12)" stroke={D.GEO_STROKE} strokeWidth="1.5"
               strokeDasharray="4 3" opacity="0.7" />

      {/* Normal vector arrow */}
      <line x1={cx} y1={cy} x2={normEndX} y2={normEndY}
            stroke={D.DIM_STROKE} strokeWidth="1.2" markerEnd="url(#arr-mir)" />
      <text x={normEndX + 4} y={normEndY - 3}
            fontSize="9" fontFamily="monospace" fill={D.DIM_STROKE} opacity="0.85">
        {normalLabel}
      </text>

      <text x="8" y="142" fontSize="9" fontFamily="monospace"
            fill="currentColor" opacity="0.45">v = normal vector of mirror plane</text>
    </svg>
  );
}
